import React from 'react';
import WaveformVisualizer from '../../Common/WaveformVisualizer.jsx';
import SoundAvatar from '../../Common/SoundAvatar.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import WinningSoundsReveal from '../../Common/WinningSoundsReveal.jsx';
import '../../../styles/theme.css';

/**
 * NameThatNoiseHost component: TV/Desktop host view for Name That Noise.
 * 
 * Constraint: The bluffing phase MUST NOT print the mystery answer on the host.
 * Only the results phase displays the ground truth.
 */
export function NameThatNoiseHost({
  room = {},
  phase: phaseProp = null,
  modeState: modeStateProp = null,
  players: playersProp = null,
  audioEngine = null,
  onForceAdvance = () => {},
  isPlayingAudio = false,
  playbackProgress = 0,
  onPlayAudio = null,
  onSetMysteryName = null,
}) {
  const lookupClip = useClipLookup();
  const [customMysteryName, setCustomMysteryName] = React.useState('');
  const [nameSaved, setNameSaved] = React.useState(false);

  const handleSaveMysteryName = (e) => {
    if (e) e.preventDefault();
    if (!customMysteryName.trim()) return;
    if (onSetMysteryName) {
      onSetMysteryName(customMysteryName.trim());
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    }
  };

  React.useEffect(() => {
    if (phaseProp === 'bluffing' || room?.currentPhase === 'bluffing') {
      setCustomMysteryName('');
      setNameSaved(false);
    }
  }, [room?.currentRound, room?.currentPhase, phaseProp]);

  const phase = phaseProp || room?.currentPhase || room?.phase || room?.modeState?.phase || 'bluffing';
  const modeState = modeStateProp || room?.modeState || {};
  const mysteryClip = modeState?.mysteryClip || { id: 'mystery', label: 'Mystery Sound', duration: 2.5 };
  const players = playersProp || room?.players || {};
  const playerList = Object.values(players)
    .filter((p) => !p.isHost)
    .sort((a, b) => (a.pIndex ?? 0) - (b.pIndex ?? 0) || (a.name || '').localeCompare(b.name || ''));
  const bluffs = modeState?.bluffs || {};
  const options = modeState?.options || [];
  const votes = modeState?.votes || {};
  const roundResults = modeState?.roundResults || null;

  return (
    <div
      className="namethatnoise-host"
      data-testid="namethatnoise-host"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--sd-ground, #08090B)',
          padding: '16px 24px',
          borderRadius: '16px',
          border: '1px solid var(--sd-line, #1A1F25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--sd-dim, #6E7982)'
          }}>
            Round {room?.currentRound || 1} · Name That Noise
          </span>
          <div style={{ width: '1px', height: '15px', background: 'var(--sd-line-2, #1C2229)' }} />
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--sd-teal, #3ACFCF)'
          }}>
            {phase.toUpperCase()}
          </span>
        </div>
        {onForceAdvance && (
          <button
            type="button"
            data-testid="host-force-advance-btn"
            onClick={onForceAdvance}
            style={{
              minHeight: '44px',
              minWidth: '44px',
              padding: '0 16px',
              borderRadius: '10px',
              background: 'var(--sd-panel-2, #0E1116)',
              border: '1px solid var(--sd-line, #1A1F25)',
              color: 'var(--sd-dim, #6E7982)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            ⏩ Force Advance
          </button>
        )}
      </div>

      {/* Masked Waveform Visualizer: NEVER shows answer during bluffing/voting */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <WaveformVisualizer
          clip={lookupClip(mysteryClip.id || mysteryClip) || mysteryClip}
          isPlaying={isPlayingAudio}
          progress={playbackProgress}
          duration={mysteryClip.duration || 2.5}
          playerName="Mystery Sound"
          playerAvatar="❓"
          clipTitle={phase === 'results' ? formatClipTitle(mysteryClip) : '??? Mystery Sound ???'}
        />

        {onPlayAudio && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              data-testid="replay-mystery-sound-btn"
              onClick={() => onPlayAudio(lookupClip(mysteryClip.id || mysteryClip) || mysteryClip)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                padding: '0 24px',
                borderRadius: '12px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                color: 'var(--sd-teal, #3ACFCF)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              🔊 Replay Mystery Sound
            </button>
          </div>
        )}
      </div>

      {/* PHASE 1: BLUFFING */}
      {(phase === 'bluffing' || phase === 'answering') && (
        <div
          data-testid="host-bluffing-phase"
          style={{
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            padding: '48px 32px',
          }}
        >
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-teal, #3ACFCF)',
            marginBottom: '8px',
          }}>
            Phase: Write Your Bluff
          </div>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            margin: '8px 0 8px 0',
            color: 'var(--sd-ink, #EDEFF2)',
            fontWeight: 800
          }}>
            What Made That Sound?
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: 'var(--sd-muted, #79838D)',
            margin: '0 0 24px 0'
          }}>
            Players are typing their bluffs on mobile. Try to write a believable answer!
          </p>

          {/* Host Secret Answer Customizer */}
          {onSetMysteryName && (
            <div
              data-testid="host-mystery-name-panel"
              style={{
                marginBottom: '32px',
                padding: '18px 24px',
                borderRadius: '14px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line-2, #1C2229)',
                maxWidth: '600px',
                margin: '0 auto 32px auto',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '11px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--sd-amber, #E0A458)',
                  fontWeight: 700,
                }}>
                  👑 Host Control · Set Mystery Sound Name
                </span>
                {nameSaved && (
                  <span style={{ fontSize: '11px', color: 'var(--sd-sage, #8FC7A0)', fontWeight: 700, fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace" }}>
                    ✓ Answer Updated!
                  </span>
                )}
              </div>
              <form onSubmit={handleSaveMysteryName} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  data-testid="host-set-mystery-name-input"
                  placeholder="Type custom correct answer name for this sound..."
                  value={customMysteryName}
                  onChange={(e) => setCustomMysteryName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'var(--sd-ground, #08090B)',
                    border: '1px solid var(--sd-line, #1A1F25)',
                    color: 'var(--sd-ink, #EDEFF2)',
                    fontSize: '13px',
                    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                    outline: 'none',
                  }}
                />
                <button
                  type="submit"
                  data-testid="host-set-mystery-name-btn"
                  disabled={!customMysteryName.trim()}
                  style={{
                    padding: '0 18px',
                    borderRadius: '8px',
                    background: customMysteryName.trim() ? 'var(--sd-amber, #E0A458)' : 'var(--sd-panel, #0D1015)',
                    color: customMysteryName.trim() ? 'var(--sd-ground, #08090B)' : 'var(--sd-dim, #6E7982)',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '12px',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: customMysteryName.trim() ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    minHeight: '44px',
                  }}
                >
                  Set Name
                </button>
              </form>
              <div style={{ fontSize: '11px', color: 'var(--sd-dim, #6E7982)', marginTop: '8px' }}>
                Overrides the ground-truth answer for players to spot during voting. Only visible to host.
              </div>
            </div>
          )}

          {/* Player submission tracker */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
            {playerList.map((p) => {
              const hasBluffed = Boolean(bluffs[p.id]);
              return (
                <div
                  key={p.id}
                  data-testid={`player-status-${p.id}`}
                  style={{
                    background: hasBluffed ? 'rgba(143, 199, 160, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${hasBluffed ? 'rgba(143, 199, 160, 0.35)' : 'var(--sd-line, #1A1F25)'}`,
                    borderRadius: '16px',
                    padding: '20px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <SoundAvatar clip={lookupClip(p.avatar)} seed={p.name} size="sm" />
                  <span style={{ fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', fontSize: '1rem' }}>{p.name}</span>
                  <span style={{
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: hasBluffed ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                  }}>
                    {hasBluffed ? 'READY ✓' : 'TYPING...'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PHASE 2: VOTING */}
      {(phase === 'voting' || phase === 'vote') && (
        <div
          data-testid="host-voting-phase"
          style={{
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            padding: '48px 32px',
          }}
        >
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-amber, #E0A458)',
            marginBottom: '8px',
          }}>
            Phase: Voting Time
          </div>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            margin: '8px 0 8px 0',
            color: 'var(--sd-ink, #EDEFF2)',
            fontWeight: 800
          }}>
            Vote for the REAL Sound Label!
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: 'var(--sd-muted, #79838D)',
            margin: '0 0 32px 0'
          }}>
            One option is ground truth. The rest are player bluffs!
          </p>

          {/* Options Display Grid */}
          <div
            data-testid="host-options-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '16px',
              textAlign: 'left'
            }}
          >
            {options.map((opt, idx) => (
              <div
                key={opt.id}
                data-testid={`host-option-item-${opt.id}`}
                style={{
                  background: 'var(--sd-panel-2, #0E1116)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '16px',
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: 'var(--sd-teal, #3ACFCF)',
                    color: 'var(--sd-ground, #08090B)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    flexShrink: 0,
                  }}
                >
                  {String.fromCharCode(65 + idx)}
                </div>
                <div style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--sd-ink, #EDEFF2)' }}>
                  {opt.text}
                </div>
              </div>
            ))}
          </div>

          {/* Player Voting Submission Tracker */}
          <div style={{ marginTop: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              borderBottom: '1px solid var(--sd-line, #1A1F25)',
              paddingBottom: '8px',
            }}>
              <span style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '11px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--sd-muted, #79838D)',
              }}>
                Player Voting Status
              </span>
              <span style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                fontWeight: 700,
                color: 'var(--sd-amber, #E0A458)',
              }}>
                Votes: {Object.keys(votes).length} / {playerList.length}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
              {playerList.map((p) => {
                const hasVoted = Boolean(votes[p.id]);
                return (
                  <div
                    key={p.id}
                    data-testid={`player-vote-status-${p.id}`}
                    style={{
                      background: hasVoted ? 'rgba(143, 199, 160, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                      border: `1px solid ${hasVoted ? 'rgba(143, 199, 160, 0.35)' : 'var(--sd-line, #1A1F25)'}`,
                      borderRadius: '16px',
                      padding: '16px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <SoundAvatar clip={lookupClip(p.avatar)} seed={p.name} size="sm" />
                    <span style={{ fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', fontSize: '0.95rem' }}>{p.name}</span>
                    <span style={{
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: hasVoted ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                    }}>
                      {hasVoted ? 'VOTED ✓' : 'DECIDING...'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* PHASE 3: RESULTS */}
      {(phase === 'results' || phase === 'reveal') && (
        <div
          data-testid="host-results-phase"
          style={{
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            padding: '48px 32px',
          }}
        >
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-sage, #8FC7A0)',
            marginBottom: '8px',
          }}>
            Ground Truth Revealed
          </div>
          <h2 style={{
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            margin: '8px 0 16px 0',
            color: 'var(--sd-sage, #8FC7A0)',
            fontWeight: 800,
            wordBreak: 'break-word',
          }}>
            {formatClipTitle(mysteryClip)}
          </h2>

          {/* Options Breakdown with Author and Voter Avatars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '24px', textAlign: 'left' }}>
            {options.map((opt) => {
              const authorPlayer = playerList.find((p) => p.id === opt.authorId);
              const voterIdsForOpt = Object.entries(votes)
                .filter(([, vOptId]) => vOptId === opt.id)
                .map(([vId]) => vId);

              return (
                <div
                  key={opt.id}
                  data-testid={`result-breakdown-${opt.id}`}
                  style={{
                    background: opt.isTruth ? 'rgba(143, 199, 160, 0.10)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${opt.isTruth ? 'rgba(143, 199, 160, 0.4)' : 'var(--sd-line-2, #1C2229)'}`,
                    borderRadius: '16px',
                    padding: '20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: opt.isTruth ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-ink, #EDEFF2)' }}>
                      {opt.text}
                    </div>
                    <span style={{
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      fontSize: '11px',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      background: opt.isTruth ? 'rgba(143, 199, 160, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                      color: opt.isTruth ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                    }}>
                      {opt.isTruth ? 'TRUTH (+1000 pts)' : authorPlayer ? `Bluff by ${authorPlayer.name}` : 'Bluff'}
                    </span>
                  </div>

                  {voterIdsForOpt.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
                      <span style={{ fontWeight: 600, color: 'var(--sd-dim, #6E7982)', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace", fontSize: '12px' }}>
                        Voted by:
                      </span>
                      {voterIdsForOpt.map((vId) => {
                        const voter = playerList.find((p) => p.id === vId);
                        return (
                          <span
                            key={vId}
                            style={{
                              background: 'var(--sd-ground, #08090B)',
                              border: '1px solid var(--sd-line, #1A1F25)',
                              borderRadius: '8px',
                              padding: '3px 10px',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: 'var(--sd-teal, #3ACFCF)'
                            }}
                          >
                            {voter ? voter.name : vId}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Scores Leaderboard */}
          {roundResults?.roundScores && (
            <div style={{ marginTop: '36px', borderTop: '1px solid var(--sd-line, #1A1F25)', paddingTop: '28px' }}>
              <h3 style={{
                fontSize: '1.4rem',
                color: 'var(--sd-amber, #E0A458)',
                margin: '0 0 20px 0',
                fontWeight: 800,
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                letterSpacing: '0.04em',
              }}>
                🏆 Scoreboard
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                {playerList.map((p) => {
                  const scoreInfo = roundResults.roundScores[p.id] || { total: 0 };
                  return (
                    <div
                      key={p.id}
                      style={{
                        background: 'var(--sd-panel-2, #0E1116)',
                        border: '1px solid var(--sd-line-2, #1C2229)',
                        borderRadius: '14px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <SoundAvatar clip={lookupClip(p.avatar)} seed={p.name} size="sm" />
                      <span style={{ fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)' }}>{p.name}</span>
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: 800,
                        color: 'var(--sd-amber, #E0A458)',
                        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      }}>
                        {p.score || 0} pts
                      </div>
                      <span style={{
                        fontSize: '0.8rem',
                        color: 'var(--sd-sage, #8FC7A0)',
                        fontWeight: 600,
                        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      }}>
                        +{scoreInfo.total} this round
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <WinningSoundsReveal room={room} audioEngine={audioEngine} />
        </div>
      )}
    </div>
  );
}

export default NameThatNoiseHost;
